import {
    AGE_FIELDS,
    GENDER_FIELDS,
    RACE_ETHNICITY_FIELDS,
} from './NeglectedOrDelinquentStudentDemographicsFields';
import {
    STUDENT_CHARACTERISTIC_FIELDS,
} from './NeglectedOrDelinquentStudentCharacteristicsFields';

export interface NeglectedOrDelinquentInformationModel {
    id: string;
    leaIdentifier: string;
    schoolYear: number;
    leaInformation: NeglectedOrDelinquentLEAInformationModel | null;
    studentPopulation: NeglectedOrDelinquentStudentPopulationModel | null;
    studentDemographics: NeglectedOrDelinquentStudentDemographicsModel | null;
    studentCharacteristics: NeglectedOrDelinquentStudentCharacteristicsModel | null;
    highSchoolCourseCredits: NeglectedOrDelinquentHighSchoolCourseCreditsModel | null;
    highSchoolDiploma: NeglectedOrDelinquentHighSchoolDiplomaModel | null;
    ged: NeglectedOrDelinquentGEDModel | null;
    postSecondaryEducation: NeglectedOrDelinquentExitOutcomeModel | null;
    jobTraining: NeglectedOrDelinquentExitOutcomeModel | null;
    employment: NeglectedOrDelinquentExitOutcomeModel | null;
    schoolReEnrollment: NeglectedOrDelinquentSchoolReEnrollmentModel | null;
    longTermStudentAcademicOutcomes:
    NeglectedOrDelinquentLongTermStudentAcademicOutcomesModel | null;
    certification: NeglectedOrDelinquentCertificationModel | null;
}

export interface NeglectedOrDelinquentLEAInformationModel {
    fundingSubPart1: boolean | null;
    fundingSubPart2: boolean | null;
    facilityType: string;
}

export interface NeglectedOrDelinquentStudentPopulationModel {
    unduplicatedStudentsServed: number | null;
    totalYearlyLengthOfStayDays: number | null;
    readonly averageLengthOfStayDays?: number | null;
}

export interface NeglectedOrDelinquentStudentDemographicsModel {
    raceEthnicity: NeglectedOrDelinquentRaceEthnicityModel | null;
    gender: NeglectedOrDelinquentGenderModel | null;
    age: NeglectedOrDelinquentAgeModel | null;
}

export interface NeglectedOrDelinquentRaceEthnicityModel {
    americanIndianOrAlaskaNative: number | null;
    asian: number | null;
    blackOrAfricanAmerican: number | null;
    hispanicOrLatino: number | null;
    nativeHawaiianOrOtherPacificIslander: number | null;
    white: number | null;
    twoOrMoreRaces: number | null;
}

export interface NeglectedOrDelinquentGenderModel {
    female: number | null;
    male: number | null;
}

export interface NeglectedOrDelinquentAgeModel {
    age3To5: number | null;
    age6: number | null;
    age7: number | null;
    age8: number | null;
    age9: number | null;
    age10: number | null;
    age11: number | null;
    age12: number | null;
    age13: number | null;
    age14: number | null;
    age15: number | null;
    age16: number | null;
    age17: number | null;
    age18: number | null;
    age19: number | null;
    age20: number | null;
    age21: number | null;
}

export interface NeglectedOrDelinquentStudentCharacteristicsModel {
    disabilitySpecialEducation: number | null;
    englishLanguageLearner: number | null;
    homeless: number | null;
    unaccompaniedHomelessYouth: number | null;
    immigrant: number | null;
    migrant: number | null;
}

export interface NeglectedOrDelinquentHighSchoolCourseCreditsModel {
    awardedCourseCredits: boolean | null;
    studentsEarnedCreditsWhileInProgram: number | null;
    studentsEarnedCreditsWithin90DaysOfExiting: number | null;
}

export interface NeglectedOrDelinquentHighSchoolDiplomaModel {
    awardedHighSchoolDiploma: boolean;
    studentsAwardedDiplomaWhileInProgram: number;
    studentsAwardedDiplomaWithin90DaysOfExiting: number;
}

export interface NeglectedOrDelinquentGEDModel {
    enrolledInGEDProgram: NeglectedOrDelinquentExitOutcomeModel | null;
    earnedGED: NeglectedOrDelinquentExitOutcomeModel | null;
}

export interface NeglectedOrDelinquentExitOutcomeModel {
    whileInProgram: number;
    within90DaysOfExiting: number;
}

export interface NeglectedOrDelinquentSchoolReEnrollmentModel {
    studentsReturnedWithin90DaysOfExiting: number;
}

export interface NeglectedOrDelinquentLongTermStudentAcademicOutcomesModel {
    longTermStudentsServed: number;
    reading: NeglectedOrDelinquentAcademicOutcomeModel | null;
    mathematics: NeglectedOrDelinquentAcademicOutcomeModel | null;
}

export interface NeglectedOrDelinquentAcademicOutcomeModel {
    studentsCompletedPreAndPostTestExams: number;
    negativeGradeLevelChanges: number;
    noGradeLevelChanges: number;
    improvedUpToOneFullGradeLevel: number;
    improvedMoreThanOneFullGradeLevel: number;
}

export interface NeglectedOrDelinquentCertificationModel {
    attestation: boolean;
    submittedBy: string | null;
    submittedDateTime: string | null;
    updatedBy: string | null;
    updatedDateTime: string | null;
}

export interface NeglectedOrDelinquentInformationFormValues {
    fundingReceived1: string;
    fundingReceived2: string;
    facilityType: string;
    unduplicatedStudentsServed: string;
    totalYearlyLengthOfStayDays: string;
    studentDemographics: NeglectedOrDelinquentStudentDemographicsFormValues;
    raceEthnicityTotal: string;
    genderTotal: string;
    ageTotal: string;
    studentCharacteristics: Record<
        keyof NeglectedOrDelinquentStudentCharacteristicsModel, string
    >;
    studentCharacteristicsLimit: string;
    highSchoolCourseCredits: Record<
        keyof NeglectedOrDelinquentHighSchoolCourseCreditsModel, string
    >;
    courseCreditsWhileLimit: string;
    courseCreditsExitLimit: string;
}

export interface NeglectedOrDelinquentStudentDemographicsFormValues {
    raceEthnicity: Record<keyof NeglectedOrDelinquentRaceEthnicityModel, string>;
    gender: Record<keyof NeglectedOrDelinquentGenderModel, string>;
    age: Record<keyof NeglectedOrDelinquentAgeModel, string>;
}

const toFormGroup = <T extends object>(
    model: Partial<Record<keyof T, number | null>> | null | undefined,
    keys: (keyof T)[]
): Record<keyof T, string> => {
    const result = {} as Record<keyof T, string>;
    keys.forEach((key) => {
        result[key] = model?.[key]?.toString() ?? '';
    });
    return result;
};

export const toNeglectedOrDelinquentInformationFormValues = (
    model: NeglectedOrDelinquentInformationModel | null
): NeglectedOrDelinquentInformationFormValues => ({
    fundingReceived1:
        model?.leaInformation?.fundingSubPart1 == null
            ? ''
            : model.leaInformation.fundingSubPart1
                ? 'Yes'
                : 'No',
    fundingReceived2:
        model?.leaInformation?.fundingSubPart2 == null
            ? ''
            : model.leaInformation.fundingSubPart2
                ? 'Yes'
                : 'No',
    facilityType: model?.leaInformation?.facilityType || '',
    unduplicatedStudentsServed:
        model?.studentPopulation?.unduplicatedStudentsServed?.toString() ?? '',
    totalYearlyLengthOfStayDays:
        model?.studentPopulation?.totalYearlyLengthOfStayDays?.toString() ?? '',
    studentDemographics: {
        raceEthnicity: toFormGroup<NeglectedOrDelinquentRaceEthnicityModel>(
            model?.studentDemographics?.raceEthnicity,
            RACE_ETHNICITY_FIELDS.map(({ key }) => key)
        ),
        gender: toFormGroup<NeglectedOrDelinquentGenderModel>(
            model?.studentDemographics?.gender, GENDER_FIELDS.map(({ key }) => key)
        ),
        age: toFormGroup<NeglectedOrDelinquentAgeModel>(
            model?.studentDemographics?.age, AGE_FIELDS.map(({ key }) => key)
        ),
    },
    raceEthnicityTotal: '',
    genderTotal: '',
    ageTotal: '',
    studentCharacteristics: toFormGroup<NeglectedOrDelinquentStudentCharacteristicsModel>(
        model?.studentCharacteristics,
        STUDENT_CHARACTERISTIC_FIELDS.map(({ key }) => key)
    ),
    studentCharacteristicsLimit: '',
    highSchoolCourseCredits: {
        awardedCourseCredits: model?.highSchoolCourseCredits?.awardedCourseCredits == null
            ? '' : model.highSchoolCourseCredits.awardedCourseCredits ? 'Yes' : 'No',
        studentsEarnedCreditsWhileInProgram:
            model?.highSchoolCourseCredits?.studentsEarnedCreditsWhileInProgram
                ?.toString() ?? '',
        studentsEarnedCreditsWithin90DaysOfExiting:
            model?.highSchoolCourseCredits?.studentsEarnedCreditsWithin90DaysOfExiting
                ?.toString() ?? '',
    },
    courseCreditsWhileLimit: '',
    courseCreditsExitLimit: '',
});

export const buildNeglectedOrDelinquentInformationModel = (
    values: NeglectedOrDelinquentInformationFormValues,
    leaIdentifier: string,
    schoolYear: number,
    id = '',
    isStateOperatedAgency = false
): NeglectedOrDelinquentInformationModel => {
    const fundingSubPart1 = isStateOperatedAgency && values.fundingReceived1 !== ''
        ? values.fundingReceived1 === 'Yes'
        : null;
    const fundingSubPart2 = values.fundingReceived2 === ''
        ? null : values.fundingReceived2 === 'Yes';
    const toModelGroup = <T extends object>(
        group: Record<keyof T, string>,
        keys: (keyof T)[]
    ): Record<keyof T, number | null> => {
        const result = {} as Record<keyof T, number | null>;
        keys.forEach((key) => {
            result[key] = group[key] === '' ? null : Number(group[key]);
        });
        return result;
    };
    return {
        id,
        leaIdentifier,
        schoolYear,
        leaInformation: {
            fundingSubPart1,
            fundingSubPart2,
            facilityType: fundingSubPart1 || fundingSubPart2 ? values.facilityType : '',
        },
        studentPopulation: {
            unduplicatedStudentsServed: values.unduplicatedStudentsServed === ''
                ? null : Number(values.unduplicatedStudentsServed),
            totalYearlyLengthOfStayDays: values.totalYearlyLengthOfStayDays === ''
                ? null : Number(values.totalYearlyLengthOfStayDays),
        },
        studentDemographics: {
            raceEthnicity: toModelGroup<NeglectedOrDelinquentRaceEthnicityModel>(
                values.studentDemographics.raceEthnicity,
                RACE_ETHNICITY_FIELDS.map(({ key }) => key)
            ),
            gender: toModelGroup<NeglectedOrDelinquentGenderModel>(
                values.studentDemographics.gender, GENDER_FIELDS.map(({ key }) => key)
            ),
            age: toModelGroup<NeglectedOrDelinquentAgeModel>(
                values.studentDemographics.age, AGE_FIELDS.map(({ key }) => key)
            ),
        },
        studentCharacteristics: toModelGroup<
            NeglectedOrDelinquentStudentCharacteristicsModel
        >(
            values.studentCharacteristics,
            STUDENT_CHARACTERISTIC_FIELDS.map(({ key }) => key)
        ),
        highSchoolCourseCredits: {
            awardedCourseCredits:
                values.highSchoolCourseCredits.awardedCourseCredits === ''
                ? null : values.highSchoolCourseCredits.awardedCourseCredits === 'Yes',
            studentsEarnedCreditsWhileInProgram:
                values.highSchoolCourseCredits.awardedCourseCredits !== 'Yes' ||
                values.highSchoolCourseCredits.studentsEarnedCreditsWhileInProgram === ''
                    ? null : Number(
                        values.highSchoolCourseCredits.studentsEarnedCreditsWhileInProgram
                    ),
            studentsEarnedCreditsWithin90DaysOfExiting:
                values.highSchoolCourseCredits.awardedCourseCredits !== 'Yes' ||
                values.highSchoolCourseCredits
                    .studentsEarnedCreditsWithin90DaysOfExiting === ''
                    ? null : Number(values.highSchoolCourseCredits
                        .studentsEarnedCreditsWithin90DaysOfExiting),
        },
        highSchoolDiploma: null,
        ged: null,
        postSecondaryEducation: null,
        jobTraining: null,
        employment: null,
        schoolReEnrollment: null,
        longTermStudentAcademicOutcomes: null,
        certification: null,
    };
};
